/**
 * PostHog — the browser. Errors, session replay, and who it happened to.
 *
 * Loaded DYNAMICALLY and only when VITE_POSTHOG_KEY is set. Importing it at the
 * top level put its vendor code in the main chunk for every visitor, including
 * the ones in environments where PostHog is switched off.
 *
 * What this is for, and what it costs: docs/OBSERVABILITY.md.
 */
import { appEnvironment } from '@/lib/environment'
import { redactDeep } from '@/lib/redact-url'

type PostHogModule = typeof import('posthog-js')

let posthog: PostHogModule['default'] | null = null

/**
 * US cloud, because that is where Gideon's PostHog account lives. The region
 * is fixed when the account is created and cannot be changed later, so this
 * default is the one that matches. `VITE_POSTHOG_HOST` overrides it.
 *
 * Getting this wrong fails SILENTLY — events go nowhere and nothing complains.
 */
const DEFAULT_HOST = 'https://us.i.posthog.com'

export async function initAnalytics(): Promise<void> {
  const key = import.meta.env.VITE_POSTHOG_KEY
  if (!key || posthog) return

  const module = await import('posthog-js')
  posthog = module.default

  posthog.init(key, {
    api_host: import.meta.env.VITE_POSTHOG_HOST ?? DEFAULT_HOST,
    // Pinned on purpose: PostHog ships behaviour changes by moving this date,
    // so leaving it off would let an SDK upgrade change what we record.
    defaults: '2026-05-30',
    capture_pageview: false, // the router owns navigation events

    // Link tokens (invitations, confirmations, password resets) never leave
    // the browser: every event, every replay snapshot and every recorded
    // network request has `token=` replaced with `token=[redacted]` first.
    before_send: (event) => (event ? redactDeep(event) : event),
    autocapture: false, // explicit events only — autocapture is noise
    persistence: 'localStorage+cookie',

    // Errors. Set explicitly rather than left to the project settings, so what
    // the app captures is decided in code and visible in review.
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      // Console noise is not a crash, and every one would be billable.
      capture_console_errors: false,
    },

    // Puts X-POSTHOG-SESSION-ID on our own API calls, so a server crash can be
    // shown beside the recording of the session that caused it. Hostnames only.
    tracing_headers: [window.location.hostname],

    session_recording: {
      // Reconnect's request carries a pasted key (D-019): its body never goes
      // into a recording, whatever the project's network-capture setting.
      maskCapturedNetworkRequestFn: (request) =>
        redactDeep(
          request.name.includes('/system/reconnect')
            ? { ...request, requestBody: null, responseBody: null }
            : request,
        ),
      maskAllInputs: true,
      maskInputOptions: { password: true, email: true },
    },
    // Recording only in production: a developer's own session is never worth a
    // billable recording. Replay must ALSO be enabled in the PostHog project
    // settings — code alone does not turn it on.
    disable_session_recording: appEnvironment !== 'production',
  })
}

export function trackPageview(path: string): void {
  posthog?.capture('$pageview', { $current_url: path })
}

export function trackEvent(name: string, properties?: Record<string, string | number | boolean>) {
  posthog?.capture(name, properties)
}

export interface IdentifiedUser {
  id: string
  organizationId: string
  /** Shown as the person's name on the replay and the error. */
  name: string
  email: string
  /**
   * Set while an admin is using the app as this person. The session belongs to
   * the staff member — that is what impersonation means — but a recording
   * labelled only with their name would be a lie about who was driving.
   */
  impersonatedBy?: string
}

/**
 * Tie everything that follows to one person, by name.
 *
 * Without this a crash report says "someone", which is the difference between a
 * bug you can chase and one you cannot. Name and email are sent so a replay is
 * identifiable at a glance; this is an internal tool whose users are named
 * staff, not anonymous visitors.
 *
 * What is NOT sent is anything they typed: replay masks all inputs, and no
 * record content goes to PostHog. See docs/OBSERVABILITY.md.
 */
export function identifyAnalyticsUser(user: IdentifiedUser | null): void {
  if (!posthog) return
  if (!user) {
    // Signed out: forget them, so the next person on this browser is not
    // recorded as the last one.
    posthog.unregister('impersonated_by')
    posthog.reset()
    return
  }

  posthog.identify(user.id, {
    name: user.name,
    email: user.email,
    organizationId: user.organizationId,
  })

  // A super-property, so EVERY event and the replay carry it — rather than a
  // person property, which would stick to the staff member permanently.
  if (user.impersonatedBy) {
    posthog.register({ impersonated_by: user.impersonatedBy })
  } else {
    posthog.unregister('impersonated_by')
  }
}

/** Report a browser error we caught ourselves, e.g. in the error boundary. */
export function captureClientError(
  error: unknown,
  properties?: Record<string, string | number | boolean>,
): void {
  posthog?.captureException(error, properties)
}

/**
 * The current recording's id, so a crashed screen can show the person a
 * reference that leads straight back to what they were doing.
 */
export function currentSessionId(): string | null {
  return posthog?.get_session_id() ?? null
}
